/**
 * The entity's officers.
 *
 * Expects the officers the api sent, one TaxProfileListRow each, the tax year the page shows, the
 * "Needs review only" filter, the page's writes, busy while a write is in flight, and the callback
 * that opens a source document.
 *
 * Renders the section heading and a ListTable of one row per officer: Name, Title, Signed the
 * return, Time devoted, W-2 compensation and the Approval column. How a row is reviewed is
 * ListTable's. The name opens ListTable's person window (personDetails), which holds the masked
 * taxpayer number; the row draws every other officer column.
 *
 * entity_officers.w2_compensation is one amount with the tax year it is for beside it
 * (w2_compensation_tax_year), not one amount per year. When the source of the amount the cell
 * shows states a year other than the one the page shows, the cell names that year after the
 * amount, as in "$120,000 (2023)". A typed amount is recorded for the year the page shows.
 */

import type { TaxProfileListRow } from '@shared/types/taxProfile';
import { formatFieldValue } from '@/pages/TaxProfile/format';
import { listColumn, rowCellSource } from '@/pages/TaxProfile/listRows';
import {
  EMPTY_CELL_DASH,
  LIST_EMPTY_SENTENCES,
  amountWithYear,
} from '@/pages/TaxProfile/wording';
import ListTable, {
  ListCellContext,
  ListTableColumn,
  ListTableProps,
} from '@/pages/TaxProfile/components/ListTable';
import { BRAND_HEADING_CLASS } from '@/pages/TaxProfile/brand';

export const OFFICERS_SECTION_ID = 'tax-profile-officers';

/**
 * The text of the W-2 compensation cell: the amount, followed by the tax year its source states in
 * parentheses when that year is not `shownTaxYear`; a dash when there is no amount.
 */
export const compensationText = (
  { value, cell, decision }: ListCellContext,
  shownTaxYear: number | null
): string => {
  if (value === null || value === undefined || value === '')
    return EMPTY_CELL_DASH;
  const amount = formatFieldValue(value, listColumn('officer_w2_compensation'));
  const year = rowCellSource(cell, decision)?.taxYear ?? null;
  return year !== null && year !== shownTaxYear
    ? amountWithYear(amount, year)
    : amount;
};

export const officerColumns = (
  shownTaxYear: number | null
): ListTableColumn[] => [
  { key: 'officer_full_legal_name', header: 'Name' },
  { key: 'officer_title', header: 'Title', widthClass: 'w-[16%]' },
  {
    key: 'officer_is_signing_officer',
    header: 'Signed the return',
    widthClass: 'w-[12%]',
  },
  {
    key: 'officer_time_devoted_pct',
    header: 'Time devoted',
    align: 'right',
    widthClass: 'w-[12%]',
  },
  {
    key: 'officer_w2_compensation',
    header: 'W-2 compensation',
    align: 'right',
    widthClass: 'w-[15%]',
    text: (context) => compensationText(context, shownTaxYear),
  },
];

export type OfficersTableProps = Omit<
  ListTableProps,
  'rows' | 'columns' | 'label' | 'nameKey' | 'noun' | 'renderFooter'
> & {
  officers: TaxProfileListRow[];
  /** Added to the section's own classes; TaxProfile.tsx passes the glow of the section in view. */
  className?: string;
};

export default function OfficersTable({
  officers,
  className = '',
  ...tableProps
}: OfficersTableProps) {
  return (
    <section
      id={OFFICERS_SECTION_ID}
      className={`mb-6 scroll-mt-4 ${className}`}
    >
      <h3 className={`font-bold mb-1 ${BRAND_HEADING_CLASS}`}>Officers</h3>
      {officers.length === 0 ? (
        <p className='text-xs opacity-70'>{LIST_EMPTY_SENTENCES.officers}</p>
      ) : (
        <ListTable
          {...tableProps}
          rows={officers}
          columns={officerColumns(tableProps.taxYear)}
          label='Officers'
          nameKey='officer_full_legal_name'
          noun='officer'
          personDetails
        />
      )}
    </section>
  );
}
